import { Component, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SalesInsightsOpenOrderRow, SalesInsightsOpenOrdersResponse } from '../../models/sales-insights.model';
import { formatSalesInsightsAmount } from '../../utils/sales-insights-format';
import { salesInsightsConstantCase } from '../../utils/sales-insights-tiles';
import { SALES_INSIGHTS_SEARCH_MIN_ROWS, salesInsightsMatches } from '../../utils/sales-insights-search';
import { SalesInsightsBaseComponent } from '../sales-insights-base/sales-insights-base.component';

export type SalesInsightsOrderListColumn =
  | 'orderId'
  | 'customerId'
  | 'customerName'
  | 'salesperson'
  | 'country'
  | 'orderDate'
  | 'deliveryDate'
  | 'valueBase'
  | 'valueLocal'
  | 'quantity'
  | 'rowCount';

export const SALES_INSIGHTS_ORDER_LIST_COLUMNS: SalesInsightsOrderListColumn[] = [
  'orderId',
  'customerId',
  'customerName',
  'salesperson',
  'country',
  'orderDate',
  'deliveryDate',
  'valueBase',
  'valueLocal',
  'quantity',
  'rowCount'
];

const DEFAULT_COLUMNS: SalesInsightsOrderListColumn[] = ['orderId', 'customerName', 'orderDate', 'deliveryDate', 'valueBase'];
const NUMBER_COLUMNS: SalesInsightsOrderListColumn[] = ['valueBase', 'valueLocal', 'quantity', 'rowCount'];
const COLUMN_LABEL_KEYS: { [column: string]: string } = {
  customerId: 'SALES_INSIGHTS_HEALTH_COL_CUSTOMER_ID',
  customerName: 'SALES_INSIGHTS_HEALTH_COL_CUSTOMER',
  salesperson: 'SALES_INSIGHTS_HEALTH_COL_SALESPERSON',
  country: 'SALES_INSIGHTS_FILTER_COUNTRY'
};
const PAGE_SIZE = 50;

export function salesInsightsOrderListColumns(configured: unknown): SalesInsightsOrderListColumn[] {
  const columns = (Array.isArray(configured) ? configured : []).filter((column): column is SalesInsightsOrderListColumn =>
    SALES_INSIGHTS_ORDER_LIST_COLUMNS.includes(column)
  );
  return columns.length ? columns : DEFAULT_COLUMNS;
}

@Component({
  selector: 'lib-sales-insights-order-list',
  standalone: false,
  templateUrl: './sales-insights-order-list.component.html',
  styleUrl: './sales-insights-order-list.component.scss'
})
export class SalesInsightsOrderListComponent extends SalesInsightsBaseComponent<SalesInsightsOpenOrdersResponse> {
  protected translate = inject(TranslateService);

  readonly formatAmount = formatSalesInsightsAmount;

  rows: SalesInsightsOpenOrderRow[] = [];
  query = '';
  private allRows: SalesInsightsOpenOrderRow[] = [];
  visibleCount = PAGE_SIZE;
  exporting = false;

  get columns(): SalesInsightsOrderListColumn[] {
    return salesInsightsOrderListColumns(this.options.columns);
  }

  get visibleRows(): SalesInsightsOpenOrderRow[] {
    return this.rows.slice(0, this.visibleCount);
  }

  get canExport(): boolean {
    return !!this.options.export && !this.loading && !this.error && this.rows.length > 0;
  }

  get totals(): string {
    const current = this.data?.current;
    if (!current) {
      return '';
    }
    return this.translate.instant('SALES_INSIGHTS_ORDERS_TOTAL', {
      count: formatSalesInsightsAmount(current.orderCount),
      amount: `${formatSalesInsightsAmount(current.valueBase)} ${this.translate.instant('SALES_INSIGHTS_UNIT_BASE_CURRENCY')}`
    });
  }

  get searchable(): boolean {
    return this.options.search !== false && !this.loading && this.allRows.length >= SALES_INSIGHTS_SEARCH_MIN_ROWS;
  }

  search(query: string): void {
    this.query = query;
    this.rows = this.matching();
    this.visibleCount = PAGE_SIZE;
  }

  showMore(): void {
    this.visibleCount += PAGE_SIZE;
  }

  isNumber(column: SalesInsightsOrderListColumn): boolean {
    return NUMBER_COLUMNS.includes(column);
  }

  columnLabelKey(column: SalesInsightsOrderListColumn): string {
    return COLUMN_LABEL_KEYS[column] || `SALES_INSIGHTS_COL_${salesInsightsConstantCase(column)}`;
  }

  cell(row: SalesInsightsOpenOrderRow, column: SalesInsightsOrderListColumn): string {
    if (this.isNumber(column)) {
      return formatSalesInsightsAmount(row[column] as number | null);
    }
    if (column === 'salesperson') {
      return row.salespersonName || row.salesperson || '';
    }
    if (column === 'customerName') {
      return row.customerName || row.customerId || '';
    }
    return (row[column] as string | null) || '';
  }

  protected isEmpty(data: SalesInsightsOpenOrdersResponse | null): boolean {
    return !data?.rows?.length;
  }

  protected onData(data: SalesInsightsOpenOrdersResponse | null): void {
    this.allRows = data?.rows || [];
    this.rows = this.matching();
    this.visibleCount = PAGE_SIZE;
  }

  private matching(): SalesInsightsOpenOrderRow[] {
    return this.allRows.filter(row =>
      salesInsightsMatches(this.query, [
        row.orderId,
        row.customerId,
        row.customerName,
        row.salesperson,
        row.salespersonName
      ])
    );
  }

  async exportExcel(): Promise<void> {
    this.exporting = true;
    try {
      const XLSX = await import('xlsx');
      const header = this.columns.map(column => this.translate.instant(this.columnLabelKey(column)));
      const body = this.rows.map(row =>
        this.columns.map(column => (this.isNumber(column) ? (row[column] as number | null) ?? null : this.cell(row, column)))
      );
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([header, ...body]), 'Data');
      XLSX.writeFile(workbook, `${this.fileName()}.xlsx`);
    } finally {
      this.exporting = false;
    }
  }

  private fileName(): string {
    const title = this.translate.instant(this.config?.title || 'SALES_INSIGHTS');
    return title.replace(/[\\/:*?"<>|]+/g, '').trim() || 'open-orders';
  }
}
